import { inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { STORAGE_KEYS_CONSTANTS } from '@core/constants';
import { Browser } from './browser';

/** Valores posibles de la decisión de consentimiento. */
type ConsentValue = 'granted' | 'denied';

/** Días de vigencia de la cookie de consentimiento. */
const CONSENT_EXPIRES_DAYS = 180;

/**
 * Gestiona el consentimiento de analítica (Google Consent Mode v2, modo avanzado).
 *
 * El tag de GA se carga siempre (ver index.html) con `analytics_storage: 'denied'`
 * por defecto. Este servicio persiste la decisión del usuario en una cookie y
 * envía el `gtag('consent', 'update', ...)` correspondiente.
 */
@Injectable({
  providedIn: 'root',
})
export class Consent {
  private readonly cookieService = inject(CookieService);
  private readonly browser = inject(Browser);

  private readonly key = STORAGE_KEYS_CONSTANTS.COOKIE_CONSENT;

  /** Indica si el usuario ya tomó una decisión (existe la cookie). */
  hasDecision(): boolean {
    if (!this.browser.isBrowser) return false;
    return this.cookieService.check(this.key);
  }

  /** El usuario acepta la analítica. */
  accept(): void {
    this.save('granted');
    this.updateConsent(true);
  }

  /** El usuario rechaza la analítica. */
  reject(): void {
    this.save('denied');
    this.updateConsent(false);
  }

  /**
   * Reaplica al cargar la app la decisión previamente guardada, para que GA
   * reciba la señal de consentimiento en cada visita.
   */
  applyStoredConsent(): void {
    if (!this.hasDecision()) return;
    this.updateConsent(this.cookieService.get(this.key) === 'granted');
  }

  /** Guarda la decisión en una cookie legible por el navegador. */
  private save(value: ConsentValue): void {
    if (!this.browser.isBrowser) return;
    this.cookieService.set(this.key, value, {
      expires: CONSENT_EXPIRES_DAYS,
      path: '/',
      sameSite: 'Lax',
    });
  }

  /** Envía la señal de consentimiento a Google (solo `analytics_storage`). */
  private updateConsent(granted: boolean): void {
    if (!this.browser.isBrowser) return;
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    gtag?.('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }
}
